import { LitElement, html, unsafeCSS, PropertyValues } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { styles as componentStyles } from '../view/cardStyles.scss';
import { GzSelectionCardItem, SelectionCardEvents, SelectionCardEventType } from '../types';

/**
 * @summary The selection card component is used within the selection card group component. It is a radio button with a label and a content area that can be selected or deselected.
 * @property label - The label of the selection card
 * @property value - The value of the selection card
 * @property selected - The selected state of the selection card
 * @property disabled - The disabled state of the selection card
 * @slot content This is the default slot that can be used to display any type of HTML inside the card. It can be set in the component via inner content (inserted between the '<gz-selection-card>' tags)
 * @cssproperty --card-background-color
 * @cssproperty --card-border-color
 * @cssproperty --card-content-color
 * @tag gz-selection-card
 * @event {SelectionCardEventType} cardSelected - fires when the card is selected. The event contains the id and value of the selected card
 */
@customElement('gz-selection-card')
export class GzSelectionCardWebComponent<T = unknown> extends LitElement implements GzSelectionCardItem {
  @property({ type: String, attribute: true, reflect: true })
  id = '';

  @property({ type: String, attribute: true, reflect: true })
  label = '';

  @property({ type: String, attribute: true, reflect: true })
  value: string | number;

  @property({ type: Boolean, attribute: true, reflect: true })
  selected = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  disabled = false;

  static get styles() {
    return [unsafeCSS(`${componentStyles}`)];
  }

  constructor() {
    super();
    this.addEventListener('click', e => this.selectCard(!this.selected));
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'radio');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', e => this.selectCard(!this.selected));
  }

  protected updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);
    if (_changedProperties.has('selected')) {
      this.setAttribute('aria-checked', this.selected ? 'true' : 'false');
    }
    if (_changedProperties.has('disabled')) {
      this.setAttribute('aria-disabled', this.disabled ? 'true' : 'false');
    }
  }

  render() {
    return html`
      <gz-radio
        .label="${this.label}"
        .value="${this.value}"
        class="label"
        ?checked="${this.selected}"
        ?disabled="${this.disabled}"
      ></gz-radio>
      <slot name="content"></slot>
    `;
  }

  public selectCard(isSelected: boolean) {
    this.selected = isSelected;
    this.dispatchEvent(
      new SelectionCardEventType(SelectionCardEvents.CARD_SELECTED, {
        detail: {
          id: this.id,
          value: this.value,
        },
        bubbles: true,
      })
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-selection-card': GzSelectionCardWebComponent;
  }
}
